import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import i18next from "i18next";
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  Menu,
  MenuItem,
  Skeleton,
  TextField,
  Drawer,
  Checkbox,
} from "@mui/material";
import {
  SearchOutlined as SearchIcon,
  CheckCircleOutline as AllClearIcon,
  CalendarTodayOutlined as CalendarIcon,
  KeyboardArrowDown as ArrowDownIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import dayjs from "dayjs";
import { useNavigate, useSearchParams } from "react-router-dom";
import { dt } from "../../../../shared/themes/redesignTokens";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import { useRedesignSetup } from "../../../../shared/hooks/useRedesignSetup";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import usePermission from "../../../../shared/hooks/usePermission";
import { Modules } from "../../../../core/models/PermissionModels";
import useDataRefresh from "../../../../shared/notifications/hooks/useDataRefresh";
import useVisibilityRefresh from "../../../../shared/hooks/useVisibilityRefresh";
import {
  fetchServiceReportsData,
  EnrichedServiceReport,
  SrTab,
} from "../../store/serviceReportsSlice";
import { WorkOrderPriorityCode, WorkOrderTypeCode, WorkOrder } from "../../models/models";
import { RootState } from "../../../../core/store";
import ServiceReportCard from "./ServiceReportCard";
import { SrAction } from "./ServiceReportOverflowMenu";
import ResolveSrDialog from "./ResolveSrDialog";
import DeleteSrDialog from "./DeleteSrDialog";
import EditSrPanel from "./EditSrPanel";
import LinkToWoPanel from "./LinkToWoPanel";
import { useToastStack, ToastStack } from "./SrActionToast";
import type { ToastVariant } from "./SrActionToast";
import serviceReportsApi from "../../api/serviceReportsApi";
import CreateWorkOrderPanel from "../workBoard/CreateWorkOrderPanel";
import BulkSelectFloatingBar, { BulkAction } from "./BulkSelectFloatingBar";
import BulkResolveDialog from "./BulkResolveDialog";
import BulkDeleteDialog from "./BulkDeleteDialog";
import BulkReopenDialog from "./BulkReopenDialog";
import BulkUnlinkDialog from "./BulkUnlinkDialog";

// ─── Tab configuration ───────────────────────────────────────

interface TabConfig {
  key: SrTab;
  labelKey: string;
  color: string;
}

const TABS: TabConfig[] = [
  { key: "untriaged", labelKey: "serviceReports.tabs.untriaged", color: "#D4572B" },
  { key: "internal", labelKey: "serviceReports.tabs.internal", color: "#3B82F6" },
  { key: "external", labelKey: "serviceReports.tabs.external", color: "#E8A817" },
  { key: "resolved", labelKey: "serviceReports.tabs.resolved", color: "#2D8C4E" },
];

// ─── Date range filter options ────────────────────────────
type DateRangeKey = "7d" | "30d" | "90d" | "all";

interface DateRangeOption {
  key: DateRangeKey;
  labelKey: string;
  days: number | null; // null = all time
}

const DATE_RANGES: DateRangeOption[] = [
  { key: "7d", labelKey: "serviceReports.dateRange.last7days", days: 7 },
  { key: "30d", labelKey: "serviceReports.dateRange.last30days", days: 30 },
  { key: "90d", labelKey: "serviceReports.dateRange.last90days", days: 90 },
  { key: "all", labelKey: "serviceReports.dateRange.allTime", days: null },
];

// ─── Sort options ────────────────────────────────────────────

type SortKey = "newest" | "oldest" | "critical" | "titleAZ";

interface SortOption {
  key: SortKey;
  labelKey: string;
}

const SORT_OPTIONS: SortOption[] = [
  { key: "newest", labelKey: "serviceReports.sort.newestFirst" },
  { key: "oldest", labelKey: "serviceReports.sort.oldestFirst" },
  { key: "critical", labelKey: "serviceReports.sort.mostCritical" },
  { key: "titleAZ", labelKey: "serviceReports.sort.titleAZ" },
];

// ─── Urgency config ──────────────────────────────────────────

interface UrgencyChipConfig {
  value: number;
  labelKey: string;
  color: string;
  tintBg: string;
}

const URGENCY_CHIPS: UrgencyChipConfig[] = [
  { value: 1, labelKey: "serviceReports.urgency.critical", color: "#DC2626", tintBg: "rgba(220,38,38,0.10)" },
  { value: 2, labelKey: "serviceReports.urgency.high", color: "#E8A817", tintBg: "rgba(232,168,23,0.10)" },
  { value: 4, labelKey: "serviceReports.urgency.normal", color: "#3B82F6", tintBg: "rgba(59,130,246,0.10)" },
  { value: 0, labelKey: "serviceReports.urgency.info", color: "#8C9099", tintBg: "rgba(140,144,153,0.10)" },
];

// ─── SR Urgency → WO Priority mapping ────────────────────────
const urgencyToPriority = (urgency?: number): number => {
  if (urgency === 1) return WorkOrderPriorityCode.Critical;
  if (urgency === 2) return WorkOrderPriorityCode.High;
  if (urgency === 4) return WorkOrderPriorityCode.Normal;
  return WorkOrderPriorityCode.Normal;
};

// Urgency sort order: Critical=1 first, then High=2, Normal=4, Info/undefined last
const urgencySortOrder = (u?: number): number => {
  if (u === 1) return 0;
  if (u === 2) return 1;
  if (u === 4) return 2;
  return 3;
};

// ─── WO Panel prefill shape ─────────────────────────────────
interface WoPrefill {
  serviceReportId?: string;
  serviceReportNumber?: string;
  machineId?: string;
  title?: string;
  description?: string;
  locationId?: string;
  priorityCode?: number;
  typeId?: number;
}

// ─── Helpers ─────────────────────────────────────────────────

/** Extract distinct machines from enriched data */
function extractMachines(items: EnrichedServiceReport[]) {
  const map = new Map<string, { id: string; model: string; sn: string; count: number }>();
  for (const item of items) {
    const sr = item.sr;
    if (!sr.machineId) continue;
    const existing = map.get(sr.machineId);
    if (existing) {
      existing.count++;
    } else {
      map.set(sr.machineId, {
        id: sr.machineId,
        model: sr.machineModel || (i18next.t("operations:serviceReports.unknownMachine") as string),
        sn: sr.machineInternalNumber || "",
        count: 1,
      });
    }
  }
  return Array.from(map.values()).sort((a, b) => b.count - a.count);
}

/** Extract distinct locations (parent > child) from enriched data */
function extractLocations(items: EnrichedServiceReport[]) {
  // Build parent → children hierarchy
  const parentMap = new Map<string, { name: string; children: Map<string, { name: string; count: number }> }>();
  for (const item of items) {
    const sr = item.sr;
    const parentName = sr.parentLocationName || sr.locationName || "";
    const childName = sr.locationName || "";
    if (!parentName && !childName) continue;

    const effectiveParent = parentName || childName;
    if (!parentMap.has(effectiveParent)) {
      parentMap.set(effectiveParent, { name: effectiveParent, children: new Map() });
    }
    const parent = parentMap.get(effectiveParent)!;
    if (childName && childName !== effectiveParent) {
      const existing = parent.children.get(childName);
      if (existing) existing.count++;
      else parent.children.set(childName, { name: childName, count: 1 });
    } else {
      // Count directly under parent
      const existing = parent.children.get(effectiveParent);
      if (existing) existing.count++;
      else parent.children.set(effectiveParent, { name: effectiveParent, count: 1 });
    }
  }
  return parentMap;
}

/** Extract distinct reporters */
function extractReporters(items: EnrichedServiceReport[]) {
  const map = new Map<string, { name: string; count: number }>();
  for (const item of items) {
    const name = item.sr.createdByName;
    if (!name) continue;
    const existing = map.get(name);
    if (existing) existing.count++;
    else map.set(name, { name, count: 1 });
  }
  return Array.from(map.values()).sort((a, b) => b.count - a.count);
}

/** Get initials from a name */
function getInitials(name: string): string {
  return name
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

/** Get a deterministic color for an avatar based on name */
function getAvatarColor(name: string): string {
  const colors = ["#D4572B", "#3B82F6", "#2D8C4E", "#8E44AD", "#E8A817", "#6B7280"];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
}

interface TabCounts {
  untriaged: number;
  internal: number;
  external: number;
  resolved: number;
  total: number;
}

// ─── Component ───────────────────────────────────────────────

const ServiceReportsPage: React.FC = () => {
  useRedesignSetup();
  const { translate } = useTranslation("operations");
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isPhone, isTablet, isDesktop } = useBreakpoint();

  const { canRead: hasServicingPermission } = usePermission(Modules.Servicing);

  const { enriched, loading, error } = useAppSelector(
    (s: RootState) => s.serviceReportsPage
  );

  // ─── Tab + search + date state ──────────────────────────────
  const [activeTab, setActiveTab] = useState<SrTab>("untriaged");
  const [searchTerm, setSearchTerm] = useState("");
  const [showResolved, setShowResolved] = useState(false);
  const [dateRange, setDateRange] = useState<DateRangeKey>("30d");
  const [dateMenuAnchor, setDateMenuAnchor] = useState<null | HTMLElement>(null);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // ─── Filter state ───────────────────────────────────────────
  const [selectedUrgencies, setSelectedUrgencies] = useState<Set<number>>(new Set());
  const [selectedMachineIds, setSelectedMachineIds] = useState<Set<string>>(new Set());
  const [selectedLocations, setSelectedLocations] = useState<Set<string>>(new Set());
  const [selectedReporters, setSelectedReporters] = useState<Set<string>>(new Set());
  const [sortKey, setSortKey] = useState<SortKey>("newest");

  // ─── Dropdown anchor state (desktop) ────────────────────────
  const [machineMenuAnchor, setMachineMenuAnchor] = useState<null | HTMLElement>(null);
  const [locationMenuAnchor, setLocationMenuAnchor] = useState<null | HTMLElement>(null);
  const [reporterMenuAnchor, setReporterMenuAnchor] = useState<null | HTMLElement>(null);
  const [sortMenuAnchor, setSortMenuAnchor] = useState<null | HTMLElement>(null);
  const [machineSearch, setMachineSearch] = useState("");
  const [reporterSearch, setReporterSearch] = useState("");

  // ─── Phone bottom sheet state ───────────────────────────────
  const [phoneFiltersOpen, setPhoneFiltersOpen] = useState(false);
  // Temporary filter state for bottom sheet (applied on "Apply Filters")
  const [tempUrgencies, setTempUrgencies] = useState<Set<number>>(new Set());
  const [tempMachineIds, setTempMachineIds] = useState<Set<string>>(new Set());
  const [tempLocations, setTempLocations] = useState<Set<string>>(new Set());
  const [tempReporters, setTempReporters] = useState<Set<string>>(new Set());
  const [tempSortKey, setTempSortKey] = useState<SortKey>("newest");

  // ─── Expand state ───────────────────────────────────────────
  const [expandedSrIds, setExpandedSrIds] = useState<Set<string>>(new Set());

  const toggleExpand = useCallback((srId: string) => {
    setExpandedSrIds((prev) => {
      const next = new Set(prev);
      if (next.has(srId)) next.delete(srId);
      else next.add(srId);
      return next;
    });
  }, []);

  // ─── SR Action toast (stacking) ────────────────────────────
  const { addToast, dismissToast, toasts } = useToastStack();

  // ─── SR Action dialog/panel state ────────────────────────
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editPanelOpen, setEditPanelOpen] = useState(false);
  const [linkToWoPanelOpen, setLinkToWoPanelOpen] = useState(false);
  const [actionTargetItem, setActionTargetItem] = useState<EnrichedServiceReport | null>(null);

  // ─── Create WO Panel state ────────────────────────────────
  const [woPanelOpen, setWoPanelOpen] = useState(false);
  const [woPrefill, setWoPrefill] = useState<WoPrefill | undefined>(undefined);

  // ─── Bulk Select state ────────────────────────────────────
  const [selectMode, setSelectMode] = useState(false);
  const [selectedSrIds, setSelectedSrIds] = useState<Set<string>>(new Set());
  const [bulkResolveOpen, setBulkResolveOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkReopenOpen, setBulkReopenOpen] = useState(false);
  const [bulkUnlinkOpen, setBulkUnlinkOpen] = useState(false);
  const [bulkWoPanelOpen, setBulkWoPanelOpen] = useState(false);
  const [bulkWoPrefill, setBulkWoPrefill] = useState<WoPrefill | undefined>(undefined);

  // Clear selection when tab changes
  useEffect(() => {
    setSelectedSrIds(new Set());
  }, [activeTab]);

  // Exit select mode clears selection
  const exitSelectMode = useCallback(() => {
    setSelectMode(false);
    setSelectedSrIds(new Set());
  }, []);

  const toggleSelectMode = useCallback(() => {
    if (selectMode) {
      exitSelectMode();
    } else {
      setSelectMode(true);
      setExpandedSrIds(new Set()); // collapse all cards when entering select mode
    }
  }, [selectMode, exitSelectMode]);

  const toggleSelectSr = useCallback((srId: string) => {
    setSelectedSrIds((prev) => {
      const next = new Set(prev);
      if (next.has(srId)) next.delete(srId);
      else next.add(srId);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedSrIds(new Set());
  }, []);

  // ─── Data fetching ────────────────────────────────────────

  const loadData = useCallback(() => {
    dispatch(fetchServiceReportsData());
  }, [dispatch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useDataRefresh("serviceReports", loadData);
  useVisibilityRefresh(loadData);

  // Handle ?published=true returnTo toast
  useEffect(() => {
    if (searchParams.get("published") === "true") {
      showActionToast("resolve", translate("serviceReports.publishedSuccess"));
      navigate("/operations/service-reports", { replace: true });
      loadData();
      setTimeout(() => loadData(), 800);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // ─── Computed data ────────────────────────────────────────

  /** Date range cutoff */
  const dateRangeCutoff = useMemo(() => {
    const opt = DATE_RANGES.find((r) => r.key === dateRange);
    if (!opt || opt.days === null) return null;
    return dayjs().subtract(opt.days, "day").startOf("day");
  }, [dateRange]);

  const tabCounts: TabCounts = useMemo(() => {
    const counts = { untriaged: 0, internal: 0, external: 0, resolved: 0, total: 0 };
    let items = enriched;
    if (dateRangeCutoff) {
      items = items.filter((item) => {
        const d = item.sr.openDate || item.sr.createdDate;
        if (!d) return true;
        return dayjs(d).isAfter(dateRangeCutoff);
      });
    }
    for (const item of items) {
      counts[item.tab]++;
      counts.total++;
    }
    return counts;
  }, [enriched, dateRangeCutoff]);

  // Extract filter options from the full dataset
  const machines = useMemo(() => extractMachines(enriched), [enriched]);
  const locations = useMemo(() => extractLocations(enriched), [enriched]);
  const reporters = useMemo(() => extractReporters(enriched), [enriched]);

  // Flatten location names for display
  const flatLocationNames = useMemo(() => {
    const names: string[] = [];
    locations.forEach((parent) => {
      parent.children.forEach((child) => {
        names.push(child.name);
      });
    });
    return names;
  }, [locations]);

  // Check if any filters are active
  const hasActiveFilters = selectedUrgencies.size > 0 || selectedMachineIds.size > 0 ||
    selectedLocations.size > 0 || selectedReporters.size > 0;

  const activeFilterCount = selectedUrgencies.size + selectedMachineIds.size +
    selectedLocations.size + selectedReporters.size;

  /** Filtered + sorted items for the active tab */
  const filteredItems = useMemo(() => {
    let items = enriched.filter((item) => item.tab === activeTab);

    // Date range filter
    if (dateRangeCutoff) {
      items = items.filter((item) => {
        const d = item.sr.openDate || item.sr.createdDate;
        if (!d) return true;
        return dayjs(d).isAfter(dateRangeCutoff);
      });
    }

    // Search filter
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      items = items.filter((item) => {
        const sr = item.sr;
        return (
          sr.title?.toLowerCase().includes(term) ||
          sr.number?.toLowerCase().includes(term) ||
          sr.manufacturerName?.toLowerCase().includes(term) ||
          sr.machineModel?.toLowerCase().includes(term) ||
          sr.locationName?.toLowerCase().includes(term) ||
          sr.createdByName?.toLowerCase().includes(term) ||
          sr.alarmCode?.toLowerCase().includes(term)
        );
      });
    }

    // Urgency filter
    if (selectedUrgencies.size > 0) {
      items = items.filter((item) => {
        const u = item.sr.urgency;
        // Info = urgency is undefined/null or not in 1,2,4
        if (selectedUrgencies.has(0)) {
          if (u === undefined || u === null || (u !== 1 && u !== 2 && u !== 4)) return true;
        }
        return u !== undefined && u !== null && selectedUrgencies.has(u);
      });
    }

    // Machine filter
    if (selectedMachineIds.size > 0) {
      items = items.filter((item) => item.sr.machineId && selectedMachineIds.has(item.sr.machineId));
    }

    // Location filter
    if (selectedLocations.size > 0) {
      items = items.filter((item) => {
        const locName = item.sr.locationName || "";
        const parentLocName = item.sr.parentLocationName || "";
        return selectedLocations.has(locName) || selectedLocations.has(parentLocName);
      });
    }

    // Reporter filter
    if (selectedReporters.size > 0) {
      items = items.filter((item) => item.sr.createdByName && selectedReporters.has(item.sr.createdByName));
    }

    // Sort
    items = [...items].sort((a, b) => {
      switch (sortKey) {
        case "newest": {
          const dA = a.sr.openDate || a.sr.createdDate || "";
          const dB = b.sr.openDate || b.sr.createdDate || "";
          return dayjs(dB).valueOf() - dayjs(dA).valueOf();
        }
        case "oldest": {
          const dA = a.sr.openDate || a.sr.createdDate || "";
          const dB = b.sr.openDate || b.sr.createdDate || "";
          return dayjs(dA).valueOf() - dayjs(dB).valueOf();
        }
        case "critical": {
          const uDiff = urgencySortOrder(a.sr.urgency) - urgencySortOrder(b.sr.urgency);
          if (uDiff !== 0) return uDiff;
          // Within same urgency, oldest first
          const dA = a.sr.openDate || a.sr.createdDate || "";
          const dB = b.sr.openDate || b.sr.createdDate || "";
          return dayjs(dA).valueOf() - dayjs(dB).valueOf();
        }
        case "titleAZ":
          return (a.sr.title || "").localeCompare(b.sr.title || "");
        default:
          return 0;
      }
    });

    return items;
  }, [enriched, activeTab, searchTerm, dateRangeCutoff, selectedUrgencies, selectedMachineIds, selectedLocations, selectedReporters, sortKey]);

  /** Get the selected EnrichedServiceReport items from the current filtered list */
  const selectedItems = useMemo(() => {
    if (selectedSrIds.size === 0) return [];
    return filteredItems.filter((item) => selectedSrIds.has(item.sr.id));
  }, [selectedSrIds, filteredItems]);

  // ─── Filter handlers ──────────────────────────────────────

  const toggleUrgency = useCallback((value: number) => {
    setSelectedUrgencies((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }, []);

  const toggleMachine = useCallback((machineId: string) => {
    setSelectedMachineIds((prev) => {
      const next = new Set(prev);
      if (next.has(machineId)) next.delete(machineId);
      else next.add(machineId);
      return next;
    });
  }, []);

  const toggleLocation = useCallback((locName: string) => {
    setSelectedLocations((prev) => {
      const next = new Set(prev);
      if (next.has(locName)) next.delete(locName);
      else next.add(locName);
      return next;
    });
  }, []);

  const toggleReporter = useCallback((name: string) => {
    setSelectedReporters((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  const clearAllFilters = useCallback(() => {
    setSelectedUrgencies(new Set());
    setSelectedMachineIds(new Set());
    setSelectedLocations(new Set());
    setSelectedReporters(new Set());
  }, []);

  // Remove a single active filter chip
  const removeFilterChip = useCallback((type: string, value: string | number) => {
    switch (type) {
      case "urgency":
        setSelectedUrgencies((prev) => { const n = new Set(prev); n.delete(value as number); return n; });
        break;
      case "machine":
        setSelectedMachineIds((prev) => { const n = new Set(prev); n.delete(value as string); return n; });
        break;
      case "location":
        setSelectedLocations((prev) => { const n = new Set(prev); n.delete(value as string); return n; });
        break;
      case "reporter":
        setSelectedReporters((prev) => { const n = new Set(prev); n.delete(value as string); return n; });
        break;
    }
  }, []);

  // ─── Phone bottom sheet handlers ───────────────────────────

  const openPhoneFilters = useCallback(() => {
    // Sync temp state from actual state
    setTempUrgencies(new Set(selectedUrgencies));
    setTempMachineIds(new Set(selectedMachineIds));
    setTempLocations(new Set(selectedLocations));
    setTempReporters(new Set(selectedReporters));
    setTempSortKey(sortKey);
    setPhoneFiltersOpen(true);
  }, [selectedUrgencies, selectedMachineIds, selectedLocations, selectedReporters, sortKey]);

  const applyPhoneFilters = useCallback(() => {
    setSelectedUrgencies(new Set(tempUrgencies));
    setSelectedMachineIds(new Set(tempMachineIds));
    setSelectedLocations(new Set(tempLocations));
    setSelectedReporters(new Set(tempReporters));
    setSortKey(tempSortKey);
    setPhoneFiltersOpen(false);
  }, [tempUrgencies, tempMachineIds, tempLocations, tempReporters, tempSortKey]);

  const resetPhoneFilters = useCallback(() => {
    setTempUrgencies(new Set());
    setTempMachineIds(new Set());
    setTempLocations(new Set());
    setTempReporters(new Set());
    setTempSortKey("newest");
  }, []);

  const toggleTempUrgency = useCallback((value: number) => {
    setTempUrgencies((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  }, []);

  // ─── Card action handlers ─────────────────────────────────

  const handleCreateWorkOrder = useCallback(
    (item: EnrichedServiceReport) => {
      const sr = item.sr;
      setWoPrefill({
        title: sr.title || undefined,
        description: sr.description || undefined,
        machineId: sr.machineId || undefined,
        locationId: sr.locationId || undefined,
        serviceReportId: sr.id || undefined,
        serviceReportNumber: sr.number || undefined,
        priorityCode: urgencyToPriority(sr.urgency),
        typeId: WorkOrderTypeCode.Reactive,
      });
      setWoPanelOpen(true);
    },
    []
  );

  const handleWoPanelClose = useCallback(() => {
    setWoPanelOpen(false);
    setWoPrefill(undefined);
  }, []);

  const handleWoCreated = useCallback(
    (wo: WorkOrder) => {
      const srNum = woPrefill?.serviceReportNumber;
      const srLabel = srNum
        ? srNum.startsWith("SR-") ? srNum : `SR-${srNum.length > 4 ? srNum.slice(-4) : srNum}`
        : "";
      setWoPanelOpen(false);
      setWoPrefill(undefined);
      addToast("success", `${wo.number || "WO-???"} created${srLabel ? ` from ${srLabel}` : ""}`);
      loadData();
      setTimeout(() => loadData(), 800);
    },
    [loadData, woPrefill]
  );

  const handleCreateServiceRequest = useCallback(
    (item: EnrichedServiceReport) => {
      const sr = item.sr;
      const params = new URLSearchParams();
      if (sr.machineId) params.set("machine", sr.machineId);
      if (sr.number) params.set("report", sr.number);
      params.set("returnTo", "/operations/service-reports");
      navigate(`/service-requests/new?${params.toString()}`);
    },
    [navigate]
  );

  const handleViewOnBoard = useCallback(
    (woId: string) => navigate(`/operations/work-orders/${woId}`),
    [navigate]
  );

  const handleViewInServiceHub = useCallback(
    (rfsId: string) => navigate(`/service-board/${rfsId}`),
    [navigate]
  );

  // ─── SR Action handlers ──────────────────────────────────

  const formatSrNum = (num: string | undefined): string => {
    if (!num) return "SR-???";
    if (num.startsWith("SR-")) return num;
    const short = num.length > 4 ? num.slice(-4) : num;
    return `SR-${short}`;
  };

  const showActionToast = useCallback((variant: ToastVariant, message: string) => {
    addToast(variant, message);
  }, [addToast]);

  const handleSingleUnlink = useCallback(
    async (item: EnrichedServiceReport) => {
      const wo = item.linkedWo;
      if (!wo || !item.sr.id) {
        showActionToast("error", translate("serviceReports.actions.unlinkFailed"));
        return;
      }
      try {
        await serviceReportsApi.unlinkSrFromWorkOrder(wo.id, item.sr.id);
        showActionToast("edit", `${formatSrNum(item.sr.number)} ${translate("serviceReports.actions.unlinkedMsg")}`);
        loadData();
        setTimeout(() => loadData(), 800);
      } catch {
        showActionToast("error", translate("serviceReports.actions.unlinkFailed"));
      }
    },
    [loadData, showActionToast, translate]
  );

  const handleSingleReopen = useCallback(
    async (item: EnrichedServiceReport) => {
      if (!item.sr.number) return;
      try {
        await serviceReportsApi.reopenSr(item.sr.number);
        showActionToast("edit", `${formatSrNum(item.sr.number)} ${translate("serviceReports.actions.reopenedMsg")}`);
        loadData();
        setTimeout(() => loadData(), 800);
      } catch {
        showActionToast("error", translate("serviceReports.actions.reopenFailed"));
      }
    },
    [loadData, showActionToast, translate]
  );

  const handleOverflowAction = useCallback(
    (action: SrAction, item: EnrichedServiceReport) => {
      setActionTargetItem(item);

      switch (action) {
        case "resolve":
          setResolveDialogOpen(true);
          break;
        case "delete": {
          // Guard: block deletion if SR has linked WO or RFS
          if (item.linkedWo || item.linkedRfs) {
            showActionToast("error", translate("serviceReports.actions.cannotDeleteLinked"));
            return;
          }
          setDeleteDialogOpen(true);
          break;
        }
        case "edit":
          setEditPanelOpen(true);
          break;
        case "changeUrgency":
          // Now handled by inline urgency popover on the card badge.
          // Overflow menu item kept for accessibility — opens edit panel as alternative.
          setEditPanelOpen(true);
          break;
        case "linkToWo":
          setLinkToWoPanelOpen(true);
          break;
        case "createWo":
          handleCreateWorkOrder(item);
          break;
        case "createRfs":
        case "escalateToRfs":
          handleCreateServiceRequest(item);
          break;
        case "unlinkFromWo":
          handleSingleUnlink(item);
          break;
        case "recall":
          // Recall = unlink from RFS. No API for this yet — show toast.
          showActionToast("error", translate("serviceReports.actions.recallNotAvailable"));
          break;
        case "reopen":
          handleSingleReopen(item);
          break;
      }
    },
    [showActionToast, translate, handleCreateWorkOrder, handleCreateServiceRequest, handleSingleUnlink, handleSingleReopen]
  );

  const handleResolved = useCallback(
    (srNumber: string) => {
      setResolveDialogOpen(false);
      setActionTargetItem(null);
      showActionToast("resolve", `${formatSrNum(srNumber)} ${translate("serviceReports.actions.resolvedSuccessfully")}`);
      loadData();
      setTimeout(() => loadData(), 800);
    },
    [loadData, showActionToast, translate]
  );

  const handleDeleted = useCallback(
    (srNumber: string) => {
      setDeleteDialogOpen(false);
      setActionTargetItem(null);
      showActionToast("delete", `${formatSrNum(srNumber)} ${translate("serviceReports.actions.deletedMsg")}`);
      loadData();
      setTimeout(() => loadData(), 800);
    },
    [loadData, showActionToast, translate]
  );

  const handleUpdated = useCallback(
    (srNumber: string) => {
      setEditPanelOpen(false);
      setActionTargetItem(null);
      showActionToast("edit", `${formatSrNum(srNumber)} ${translate("serviceReports.actions.updatedMsg")}`);
      loadData();
      setTimeout(() => loadData(), 800);
    },
    [loadData, showActionToast, translate]
  );

  const handleLinked = useCallback(
    (srNumber: string, woNumber: string) => {
      setLinkToWoPanelOpen(false);
      setActionTargetItem(null);
      showActionToast(
        "edit",
        `${formatSrNum(srNumber)} ${translate("serviceReports.actions.linkedTo")} ${woNumber}`
      );
      loadData();
      setTimeout(() => loadData(), 800);
    },
    [loadData, showActionToast, translate]
  );

  // handleActionToastClose removed — toasts auto-dismiss via stack

  // ─── Quick Urgency Change handler ────────────────────────
  const handleUrgencyChange = useCallback(
    async (item: EnrichedServiceReport, newUrgency: number) => {
      const sr = item.sr;
      if (!sr.number) return;

      const oldUrgency = sr.urgency;
      // Skip if same urgency
      const normalizedOld = oldUrgency === 1 || oldUrgency === 2 || oldUrgency === 4 ? oldUrgency : 0;
      const normalizedNew = newUrgency === 1 || newUrgency === 2 || newUrgency === 4 ? newUrgency : 0;
      if (normalizedOld === normalizedNew) return;

      // Optimistic update — mutate enriched data in-place for instant UI feedback
      // The API value for "Info" is undefined/null/0 — backend accepts urgency=0
      const apiUrgency = newUrgency === 0 ? undefined : newUrgency;

      try {
        await serviceReportsApi.updateSr(sr.number, {
          title: sr.title || "",
          description: sr.description || "",
          notes: sr.notes || undefined,
          urgency: apiUrgency,
          alarmCode: sr.alarmCode || undefined,
        });
        showActionToast("edit", `${formatSrNum(sr.number)} ${translate("serviceReports.actions.urgencyChanged")}`);
        loadData();
        setTimeout(() => loadData(), 800);
      } catch {
        showActionToast("error", translate("serviceReports.actions.urgencyChangeFailed"));
        loadData(); // Revert by re-fetching
      }
    },
    [loadData, showActionToast, translate]
  );

  // ─── Inline Add Note handler ─────────────────────────────
  const handleAddNote = useCallback(
    async (item: EnrichedServiceReport, noteText: string) => {
      const sr = item.sr;
      if (!sr.number) throw new Error("Missing SR number");

      // Build the new note entry with author + timestamp header
      const authorName = "You"; // Will be replaced server-side with actual user name
      const timestamp = dayjs().format("DD MMM YYYY, HH:mm");
      const noteHeader = `${authorName} — ${timestamp}`;
      const newNoteBlock = `${noteHeader}\n${noteText}`;

      // Append to existing notes
      const existingNotes = sr.notes?.trim() || "";
      const updatedNotes = existingNotes
        ? `${existingNotes}\n\n${newNoteBlock}`
        : newNoteBlock;

      await serviceReportsApi.updateSr(sr.number, {
        title: sr.title || "",
        description: sr.description || "",
        notes: updatedNotes,
        urgency: sr.urgency,
        alarmCode: sr.alarmCode || undefined,
      });

      showActionToast("edit", `${formatSrNum(sr.number)} ${translate("serviceReports.actions.noteAdded")}`);
      loadData();
      setTimeout(() => loadData(), 800);
    },
    [loadData, showActionToast, translate]
  );

  // ─── Bulk Action handlers ─────────────────────────────────

  const handleBulkResolveAll = useCallback(() => {
    if (selectedItems.length === 0) return;
    setBulkResolveOpen(true);
  }, [selectedItems]);

  const handleBulkResolved = useCallback(
    (count: number) => {
      setBulkResolveOpen(false);
      showActionToast(
        "resolve",
        translate("serviceReports.bulk.resolvedCount", { count })
      );
      exitSelectMode();
      loadData();
      setTimeout(() => loadData(), 800);
    },
    [loadData, showActionToast, translate, exitSelectMode]
  );

  const handleBulkCreateWoForAll = useCallback(() => {
    if (selectedItems.length === 0) return;
    const first = selectedItems[0];
    const sr = first.sr;
    setBulkWoPrefill({
      title: sr.title || undefined,
      description: sr.description || undefined,
      machineId: sr.machineId || undefined,
      locationId: sr.locationId || undefined,
      serviceReportId: sr.id || undefined,
      serviceReportNumber: sr.number || undefined,
      priorityCode: urgencyToPriority(sr.urgency),
      typeId: WorkOrderTypeCode.Reactive,
    });
    setBulkWoPanelOpen(true);
  }, [selectedItems]);

  const handleBulkWoCreated = useCallback(
    async (wo: WorkOrder) => {
      setBulkWoPanelOpen(false);
      setBulkWoPrefill(undefined);

      // Link all remaining selected SRs to this WO (first one is already linked via prefill)
      const itemsToLink = selectedItems.filter(
        (item) => item.sr.id !== wo.serviceReportId
      );

      let linked = 1; // first one was linked during creation
      for (const item of itemsToLink) {
        if (!item.sr.id || !item.sr.number) continue;
        try {
          await serviceReportsApi.linkSrToWorkOrder(wo.id, {
            serviceReportId: item.sr.id,
            serviceReportNumber: item.sr.number,
          });
          linked++;
        } catch {
          // Continue with others
        }
      }

      showActionToast(
        "edit",
        translate("serviceReports.bulk.woCreatedWithLinks")
          .replace("{{woNumber}}", wo.number || "WO-???")
          .replace("{{count}}", String(linked))
      );
      exitSelectMode();
      loadData();
      setTimeout(() => loadData(), 800);
    },
    [selectedItems, loadData, showActionToast, translate, exitSelectMode]
  );

  const handleBulkWoPanelClose = useCallback(() => {
    setBulkWoPanelOpen(false);
    setBulkWoPrefill(undefined);
  }, []);

  const handleBulkDeleteAll = useCallback(() => {
    if (selectedItems.length === 0) return;
    // Check if any selected SR has linked items
    const hasLinked = selectedItems.some((item) => item.linkedWo || item.linkedRfs);
    if (hasLinked) {
      showActionToast("error", translate("serviceReports.actions.cannotDeleteLinked"));
      return;
    }
    setBulkDeleteOpen(true);
  }, [selectedItems, showActionToast, translate]);

  const handleBulkDeleteConfirm = useCallback(async () => {
    setBulkDeleteOpen(false);

    let deleted = 0;
    for (const item of selectedItems) {
      if (!item.sr.number) continue;
      try {
        await serviceReportsApi.deleteSr(item.sr.number);
        deleted++;
      } catch {
        // Continue with others
      }
    }

    if (deleted > 0) {
      showActionToast(
        "delete",
        translate("serviceReports.bulk.deletedCount", { count: deleted })
      );
    } else {
      showActionToast("error", translate("serviceReports.bulk.deleteFailedAll"));
    }

    exitSelectMode();
    loadData();
    setTimeout(() => loadData(), 800);
  }, [selectedItems, loadData, showActionToast, translate, exitSelectMode]);

  // ─── Bulk Reopen handler ──────────────────────────────────

  const handleBulkReopenAll = useCallback(() => {
    if (selectedItems.length === 0) return;
    setBulkReopenOpen(true);
  }, [selectedItems]);

  const handleBulkReopened = useCallback(
    (count: number) => {
      setBulkReopenOpen(false);
      showActionToast(
        "edit",
        translate("serviceReports.bulk.reopenedCount", { count })
      );
      exitSelectMode();
      loadData();
      setTimeout(() => loadData(), 800);
    },
    [loadData, showActionToast, translate, exitSelectMode]
  );

  // ─── Bulk Unlink handler ──────────────────────────────────

  const handleBulkUnlinkAll = useCallback(() => {
    if (selectedItems.length === 0) return;
    setBulkUnlinkOpen(true);
  }, [selectedItems]);

  const handleBulkUnlinked = useCallback(
    (count: number) => {
      setBulkUnlinkOpen(false);
      showActionToast(
        "edit",
        translate("serviceReports.bulk.unlinkedCount", { count })
      );
      exitSelectMode();
      loadData();
      setTimeout(() => loadData(), 800);
    },
    [loadData, showActionToast, translate, exitSelectMode]
  );

  // ─── Bulk Create RFS handler ─────────────────────────────

  const handleBulkCreateRfs = useCallback(() => {
    if (selectedItems.length === 0) return;
    // Navigate to RFS creation with first selected SR pre-filled
    const first = selectedItems[0];
    handleCreateServiceRequest(first);
    exitSelectMode();
  }, [selectedItems, handleCreateServiceRequest, exitSelectMode]);

  // ─── Bulk Recall handler ──────────────────────────────────

  const handleBulkRecall = useCallback(() => {
    // No API for recall (unlinking from RFS) yet
    showActionToast("error", translate("serviceReports.actions.recallNotAvailable"));
  }, [showActionToast, translate]);

  // ─── Unified Bulk Action dispatcher ───────────────────────

  const handleBulkAction = useCallback(
    (action: BulkAction) => {
      switch (action) {
        case "resolveAll":
          handleBulkResolveAll();
          break;
        case "createWoForAll":
          handleBulkCreateWoForAll();
          break;
        case "deleteAll":
          handleBulkDeleteAll();
          break;
        case "createRfsForAll":
        case "escalateToRfs":
          handleBulkCreateRfs();
          break;
        case "unlinkFromWo":
          handleBulkUnlinkAll();
          break;
        case "recall":
          handleBulkRecall();
          break;
        case "reopenAll":
          handleBulkReopenAll();
          break;
      }
    },
    [
      handleBulkResolveAll,
      handleBulkCreateWoForAll,
      handleBulkDeleteAll,
      handleBulkCreateRfs,
      handleBulkUnlinkAll,
      handleBulkRecall,
      handleBulkReopenAll,
    ]
  );

  // ─── Render ───────────────────────────────────────────────

  const showInitialLoading = loading && enriched.length === 0;

  // Build active filter chips data
  const activeChips: { type: string; value: string | number; label: string }[] = useMemo(() => {
    const chips: { type: string; value: string | number; label: string }[] = [];
    selectedUrgencies.forEach((u) => {
      const cfg = URGENCY_CHIPS.find((c) => c.value === u);
      chips.push({ type: "urgency", value: u, label: cfg ? translate(cfg.labelKey) : String(u) });
    });
    selectedMachineIds.forEach((id) => {
      const m = machines.find((m) => m.id === id);
      chips.push({ type: "machine", value: id, label: m?.model || id });
    });
    selectedLocations.forEach((loc) => {
      chips.push({ type: "location", value: loc, label: loc });
    });
    selectedReporters.forEach((name) => {
      chips.push({ type: "reporter", value: name, label: name });
    });
    return chips;
  }, [selectedUrgencies, selectedMachineIds, selectedLocations, selectedReporters, machines, translate]);

  return (
    <Box
      sx={{
        fontFamily: dt.font,
        "& *:not(.MuiSvgIcon-root)": { fontFamily: dt.font },
        backgroundColor: "#F2F0EB",
        backgroundImage: "radial-gradient(rgba(212,87,43,0.03) 1px, transparent 1px)",
        backgroundSize: "22px 22px",
        minHeight: "100vh",
      }}
    >
      {/* Page container */}
      <Box
        sx={{
          maxWidth: "1200px",
          mx: "auto",
          px: isPhone ? "12px" : isTablet ? "18px" : "24px",
          py: isPhone ? "16px" : isTablet ? "20px" : "28px",
          width: "100%",
        }}
      >
        {/* ─── White Bar (Tabs + Filters) ─── */}
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            padding: isPhone ? "0 12px" : isTablet ? "0 18px" : "0 24px",
            borderRadius: "10px",
            overflow: "hidden",
            mb: isPhone ? "12px" : "16px",
          }}
        >
          {/* ─── Row 1: Triage Tabs + Sort/Date/Search ─── */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              py: isPhone ? "10px" : "12px",
              flexWrap: "wrap",
              animation: "headerReveal 0.4s ease both",
            }}
          >
            {/* Tabs — left side */}
            <Box sx={{ display: "flex", gap: "8px", flexWrap: isPhone ? "nowrap" : "wrap", overflow: isPhone ? "auto" : "visible", flexShrink: 0 }}>
              {showInitialLoading ? (
                <>
                  <Skeleton variant="rounded" width={100} height={36} sx={{ borderRadius: "6px" }} />
                  <Skeleton variant="rounded" width={90} height={36} sx={{ borderRadius: "6px" }} />
                  <Skeleton variant="rounded" width={90} height={36} sx={{ borderRadius: "6px" }} />
                </>
              ) : (
                TABS.map((tab) => {
                  // Hide Resolved tab by default unless toggled or active
                  if (tab.key === "resolved" && !showResolved && activeTab !== "resolved") {
                    return (
                      <Box
                        key={tab.key}
                        onClick={() => {
                          setShowResolved(true);
                          setActiveTab("resolved");
                        }}
                        sx={{
                          fontFamily: dt.font,
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#444F57",
                          backgroundColor: "#F2F0EB",
                          border: "none",
                          borderRadius: "6px",
                          padding: "8px 18px",
                          cursor: "pointer",
                          userSelect: "none",
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "8px",
                          transition: "all 0.15s ease",
                          whiteSpace: "nowrap",
                          boxSizing: "border-box",
                          "&:hover": { backgroundColor: "#EDEAE5" },
                        }}
                      >
                        {translate(tab.labelKey)}
                        {tabCounts.resolved > 0 && (
                          <Box
                            component="span"
                            sx={{
                              fontFamily: dt.fontMono,
                              fontSize: "12px",
                              fontWeight: 700,
                              color: "#6B7280",
                            }}
                          >
                            {tabCounts.resolved}
                          </Box>
                        )}
                      </Box>
                    );
                  }

                  const isActive = activeTab === tab.key;
                  const count = tabCounts[tab.key];

                  return (
                    <Box
                      key={tab.key}
                      onClick={() => setActiveTab(tab.key)}
                      sx={{
                        fontFamily: dt.font,
                        fontSize: "14px",
                        fontWeight: 500,
                        color: isActive ? "#FFFFFF" : "#444F57",
                        backgroundColor: isActive ? "#0C1117" : "#F2F0EB",
                        border: "none",
                        borderRadius: "6px",
                        padding: "8px 18px",
                        cursor: "pointer",
                        userSelect: "none",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "8px",
                        transition: "all 0.15s ease",
                        whiteSpace: "nowrap",
                        boxSizing: "border-box",
                        "&:hover": { backgroundColor: isActive ? "#1a1a2e" : "#EDEAE5" },
                      }}
                    >
                      {translate(tab.labelKey)}
                      <Box
                        component="span"
                        sx={{
                          fontFamily: dt.fontMono,
                          fontSize: "12px",
                          fontWeight: 700,
                          color: isActive ? "rgba(255,255,255,0.7)" : "#6B7280",
                        }}
                      >
                        {count}
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>

            {/* Right side controls — Sort + Date + Search (desktop only, inline with tabs) */}
            {isDesktop && !showInitialLoading && (
              <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: "6px" }}>
                {/* Sort dropdown */}
                <Box
                  onClick={(e) => setSortMenuAnchor(e.currentTarget)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    padding: "8px 14px",
                    borderRadius: "8px",
                    backgroundColor: "#F7F5F0",
                    fontFamily: dt.font,
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#8C9099",
                    cursor: "pointer",
                    userSelect: "none",
                    whiteSpace: "nowrap",
                    height: "36px",
                    boxSizing: "border-box",
                    transition: "all 0.15s ease",
                    "&:hover": { backgroundColor: "#EDEAE5", color: "#444F57" },
                  }}
                >
                  <span style={{ color: "#9CA3AF", marginRight: "2px" }}>
                    {translate("serviceReports.sort.label")}
                  </span>
                  <span style={{ color: "#444F57", fontWeight: 600 }}>
                    {translate(SORT_OPTIONS.find((s) => s.key === sortKey)?.labelKey || "")}
                  </span>
                  <ArrowDownIcon sx={{ fontSize: 16, color: "#9CA3AF" }} />
                </Box>

                {/* Date range */}
                <Box
                  onClick={(e) => setDateMenuAnchor(e.currentTarget)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "8px 14px",
                    borderRadius: "8px",
                    backgroundColor: "#F7F5F0",
                    fontFamily: dt.fontMono,
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#444F57",
                    cursor: "pointer",
                    userSelect: "none",
                    whiteSpace: "nowrap",
                    height: "36px",
                    boxSizing: "border-box",
                    transition: "all 0.15s ease",
                    "&:hover": { backgroundColor: "#EDEAE5" },
                  }}
                >
                  <CalendarIcon sx={{ fontSize: 15, color: "#D4572B" }} />
                  {translate(DATE_RANGES.find((r) => r.key === dateRange)?.labelKey || "serviceReports.dateRange.last30days")}
                </Box>

                {/* Search */}
                <Box sx={{ display: "flex", alignItems: "center", position: "relative" }}>
                  {searchExpanded ? (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        backgroundColor: "#F7F5F0",
                        border: "1px solid #D4572B",
                        borderRadius: "8px",
                        padding: "6px 14px",
                        height: "36px",
                        boxSizing: "border-box",
                        boxShadow: "0 0 0 3px rgba(212,87,43,0.08)",
                        animation: "fadeIn 0.2s ease",
                      }}
                    >
                      <SearchIcon sx={{ fontSize: 16, color: "#D4572B" }} />
                      <input
                        ref={searchInputRef}
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Escape") { setSearchTerm(""); setSearchExpanded(false); } }}
                        placeholder={translate("serviceReports.searchPlaceholder")}
                        autoFocus
                        style={{
                          border: "none",
                          outline: "none",
                          background: "transparent",
                          fontFamily: "var(--font-sans)",
                          fontSize: "13px",
                          color: "#0C1117",
                          width: isPhone ? "140px" : isTablet ? "160px" : "180px",
                        }}
                      />
                      <CloseIcon
                        sx={{
                          fontSize: 14,
                          color: "#9CA3AF",
                          cursor: "pointer",
                          "&:hover": { color: "#D4572B" },
                        }}
                        onClick={() => { setSearchTerm(""); setSearchExpanded(false); }}
                      />
                    </Box>
                  ) : (
                    <Box
                      onClick={() => { setSearchExpanded(true); setTimeout(() => searchInputRef.current?.focus(), 100); }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        backgroundColor: "#F7F5F0",
                        borderRadius: "8px",
                        padding: "8px 14px",
                        cursor: "pointer",
                        fontFamily: dt.font,
                        fontSize: "13px",
                        color: "#9CA3AF",
                        height: "36px",
                        boxSizing: "border-box",
                        transition: "all 0.15s ease",
                        "&:hover": { backgroundColor: "#EDEAE5", color: "#D4572B" },
                      }}
                    >
                      <SearchIcon sx={{ fontSize: 16 }} />
                      {translate("serviceReports.searchShort") || "Search..."}
                    </Box>
                  )}
                </Box>

                {/* Select Mode toggle — desktop */}
                <Box
                  onClick={toggleSelectMode}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    fontFamily: dt.font,
                    fontSize: "13px",
                    fontWeight: 500,
                    color: selectMode ? "#FFFFFF" : "#D4572B",
                    backgroundColor: selectMode ? "#D4572B" : "transparent",
                    border: selectMode ? "none" : "1px solid #D4572B",
                    borderRadius: "8px",
                    height: "36px",
                    padding: selectMode ? "0 14px" : "0 13px",
                    cursor: "pointer",
                    userSelect: "none",
                    transition: "all 0.2s ease",
                    whiteSpace: "nowrap",
                    boxSizing: "border-box",
                    "&:hover": {
                      backgroundColor: selectMode ? "#B84A24" : "rgba(212,87,43,0.06)",
                    },
                  }}
                >
                  {selectMode && <CheckIcon sx={{ fontSize: "14px" }} />}
                  {translate("serviceReports.bulk.selectMode")}
                </Box>

                {/* Hint text when select mode is active — desktop */}
                {selectMode && (
                  <Box
                    sx={{
                      fontFamily: dt.font,
                      fontSize: "12px",
                      color: "#8C9099",
                      fontWeight: 400,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {translate("serviceReports.bulk.clickToSelect")}
                  </Box>
                )}
              </Box>
            )}

            {/* Phone: date/search + filter button */}
            {isPhone && !showInitialLoading && (
              <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: "6px" }}>
                <Box
                  onClick={(e) => setDateMenuAnchor(e.currentTarget)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    padding: "8px 10px",
                    borderRadius: "8px",
                    backgroundColor: "#F7F5F0",
                    fontFamily: dt.fontMono,
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#444F57",
                    cursor: "pointer",
                    userSelect: "none",
                    whiteSpace: "nowrap",
                    height: "36px",
                    boxSizing: "border-box",
                    transition: "all 0.15s ease",
                    "&:hover": { backgroundColor: "#EDEAE5" },
                  }}
                >
                  <CalendarIcon sx={{ fontSize: 15, color: "#D4572B" }} />
                  {translate(DATE_RANGES.find((r) => r.key === dateRange)?.labelKey || "serviceReports.dateRange.last30days")}
                </Box>

                {/* Filter button with badge */}
                <Box
                  onClick={openPhoneFilters}
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "30px",
                    height: "30px",
                    border: "1px solid #E0DDD7",
                    borderRadius: "8px",
                    cursor: "pointer",
                    backgroundColor: hasActiveFilters ? "#D4572B" : "transparent",
                    transition: "all 0.15s ease",
                    "&:hover": { backgroundColor: hasActiveFilters ? "#B84A24" : "#F5F4F1" },
                  }}
                >
                  <FilterListIcon sx={{ fontSize: "16px", color: hasActiveFilters ? "#FFFFFF" : "#4A4F57" }} />
                  {activeFilterCount > 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "-4px",
                        right: "-4px",
                        width: "16px",
                        height: "16px",
                        borderRadius: "50%",
                        backgroundColor: "#D4572B",
                        border: "2px solid #FFFFFF",
                        color: "#FFFFFF",
                        fontSize: "9px",
                        fontWeight: 700,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontFamily: dt.fontMono,
                      }}
                    >
                      {activeFilterCount}
                    </Box>
                  )}
                </Box>

                {/* Select Mode toggle — phone */}
                <Box
                  onClick={toggleSelectMode}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "30px",
                    height: "30px",
                    border: selectMode ? "none" : "1px solid #D4572B",
                    borderRadius: "8px",
                    cursor: "pointer",
                    backgroundColor: selectMode ? "#D4572B" : "transparent",
                    transition: "all 0.15s ease",
                    "&:hover": { backgroundColor: selectMode ? "#B84A24" : "rgba(212,87,43,0.06)" },
                  }}
                >
                  <CheckIcon sx={{ fontSize: "16px", color: selectMode ? "#FFFFFF" : "#D4572B" }} />
                </Box>
              </Box>
            )}
          </Box>

          {/* Phone: search bar below tabs */}
          {isPhone && !showInitialLoading && (
            <Box sx={{ pb: "8px" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  backgroundColor: "#F7F5F0",
                  borderRadius: "8px",
                  padding: "6px 14px",
                  height: "36px",
                  boxSizing: "border-box",
                }}
              >
                <SearchIcon sx={{ fontSize: 16, color: "#9CA3AF" }} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={translate("serviceReports.searchPlaceholder")}
                  style={{
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    fontFamily: "var(--font-sans)",
                    fontSize: "13px",
                    color: "#0C1117",
                    width: "100%",
                  }}
                />
                {searchTerm && (
                  <CloseIcon
                    sx={{
                      fontSize: 14,
                      color: "#9CA3AF",
                      cursor: "pointer",
                      "&:hover": { color: "#D4572B" },
                    }}
                    onClick={() => setSearchTerm("")}
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Tablet: Sort/Date/Search/Select row below tabs */}
          {isTablet && !showInitialLoading && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                pb: "10px",
                flexWrap: "wrap",
                justifyContent: "flex-end",
                animation: "fadeSlideUp 0.25s ease both",
              }}
            >
              {/* Sort dropdown */}
              <Box
                onClick={(e) => setSortMenuAnchor(e.currentTarget)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  backgroundColor: "#F7F5F0",
                  fontFamily: dt.font,
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#8C9099",
                  cursor: "pointer",
                  userSelect: "none",
                  whiteSpace: "nowrap",
                  height: "36px",
                  boxSizing: "border-box",
                  transition: "all 0.15s ease",
                  "&:hover": { backgroundColor: "#EDEAE5", color: "#444F57" },
                }}
              >
                <span style={{ color: "#9CA3AF", marginRight: "2px" }}>
                  {translate("serviceReports.sort.label")}
                </span>
                <span style={{ color: "#444F57", fontWeight: 600 }}>
                  {translate(SORT_OPTIONS.find((s) => s.key === sortKey)?.labelKey || "")}
                </span>
                <ArrowDownIcon sx={{ fontSize: 16, color: "#9CA3AF" }} />
              </Box>

              {/* Date range */}
              <Box
                onClick={(e) => setDateMenuAnchor(e.currentTarget)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  backgroundColor: "#F7F5F0",
                  fontFamily: dt.fontMono,
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#444F57",
                  cursor: "pointer",
                  userSelect: "none",
                  whiteSpace: "nowrap",
                  height: "36px",
                  boxSizing: "border-box",
                  transition: "all 0.15s ease",
                  "&:hover": { backgroundColor: "#EDEAE5" },
                }}
              >
                <CalendarIcon sx={{ fontSize: 15, color: "#D4572B" }} />
                {translate(DATE_RANGES.find((r) => r.key === dateRange)?.labelKey || "serviceReports.dateRange.last30days")}
              </Box>

              {/* Search — icon-only toggle for tablet */}
              <Box sx={{ display: "flex", alignItems: "center", position: "relative" }}>
                {searchExpanded ? (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      backgroundColor: "#F7F5F0",
                      border: "1px solid #D4572B",
                      borderRadius: "8px",
                      padding: "6px 12px",
                      height: "36px",
                      boxSizing: "border-box",
                      boxShadow: "0 0 0 3px rgba(212,87,43,0.08)",
                      animation: "fadeIn 0.2s ease",
                    }}
                  >
                    <SearchIcon sx={{ fontSize: 16, color: "#D4572B" }} />
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Escape") { setSearchTerm(""); setSearchExpanded(false); } }}
                      placeholder={translate("serviceReports.searchPlaceholder")}
                      autoFocus
                      style={{
                        border: "none",
                        outline: "none",
                        background: "transparent",
                        fontFamily: "var(--font-sans)",
                        fontSize: "13px",
                        color: "#0C1117",
                        width: "160px",
                      }}
                    />
                    <CloseIcon
                      sx={{
                        fontSize: 14,
                        color: "#9CA3AF",
                        cursor: "pointer",
                        "&:hover": { color: "#D4572B" },
                      }}
                      onClick={() => { setSearchTerm(""); setSearchExpanded(false); }}
                    />
                  </Box>
                ) : (
                  <Box
                    onClick={() => { setSearchExpanded(true); setTimeout(() => searchInputRef.current?.focus(), 100); }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "36px",
                      height: "36px",
                      backgroundColor: "#F7F5F0",
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                      "&:hover": { backgroundColor: "#EDEAE5", color: "#D4572B" },
                    }}
                  >
                    <SearchIcon sx={{ fontSize: 16, color: "#9CA3AF" }} />
                  </Box>
                )}
              </Box>

              {/* Select Mode toggle — tablet */}
              <Box
                onClick={toggleSelectMode}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                  fontFamily: dt.font,
                  fontSize: "13px",
                  fontWeight: 500,
                  color: selectMode ? "#FFFFFF" : "#D4572B",
                  backgroundColor: selectMode ? "#D4572B" : "transparent",
                  border: selectMode ? "none" : "1px solid #D4572B",
                  borderRadius: "8px",
                  height: "36px",
                  padding: selectMode ? "0 14px" : "0 13px",
                  cursor: "pointer",
                  userSelect: "none",
                  transition: "all 0.2s ease",
                  whiteSpace: "nowrap",
                  boxSizing: "border-box",
                  "&:hover": {
                    backgroundColor: selectMode ? "#B84A24" : "rgba(212,87,43,0.06)",
                  },
                }}
              >
                {selectMode && <CheckIcon sx={{ fontSize: "14px" }} />}
                {translate("serviceReports.bulk.selectMode")}
              </Box>

              {/* Hint text when select mode is active */}
              {selectMode && (
                <Box
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "12px",
                    color: "#8C9099",
                    fontWeight: 400,
                    whiteSpace: "nowrap",
                  }}
                >
                  {translate("serviceReports.bulk.clickToSelect")}
                </Box>
              )}
            </Box>
          )}

          {/* Divider between Row 1 and Row 2 */}
          {!isPhone && !showInitialLoading && (
            <Box sx={{ height: "1px", backgroundColor: "#E8E5DF" }} />
          )}

          {/* ─── Row 2: Filter Bar (Desktop/Tablet only) ─── */}
          {!isPhone && !showInitialLoading && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: isTablet ? "6px" : "8px",
                flexWrap: "wrap",
                padding: isTablet ? "10px 0" : "12px 20px",
                animation: "fadeSlideUp 0.3s ease 0.1s both",
              }}
            >
              {/* Urgency multi-select chips */}
              {URGENCY_CHIPS.map((chip) => {
                const isSelected = selectedUrgencies.has(chip.value);
                return (
                  <Box
                    key={chip.value}
                    onClick={() => toggleUrgency(chip.value)}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 16px",
                      borderRadius: "20px",
                      border: isSelected ? "1.5px solid #0C1117" : "1.5px solid #E8E5DF",
                      backgroundColor: isSelected ? "#0C1117" : "#FFFFFF",
                      color: isSelected ? "#FFFFFF" : "#444F57",
                      fontFamily: dt.font,
                      fontSize: "13px",
                      fontWeight: 500,
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                      whiteSpace: "nowrap",
                      userSelect: "none",
                      height: "32px",
                      boxSizing: "border-box",
                      "&:hover": {
                        borderColor: isSelected ? "#0C1117" : "#9CA3AF",
                        backgroundColor: isSelected ? "#1a1a2e" : "#F7F5F0",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: isSelected ? "#FFFFFF" : chip.color,
                        flexShrink: 0,
                        border: isSelected ? "none" : `1.5px solid ${chip.color}`,
                        boxSizing: "border-box",
                      }}
                    />
                    {translate(chip.labelKey)}
                  </Box>
                );
              })}

              {/* Separator */}
              <Box sx={{ width: "1px", height: "20px", backgroundColor: "#E8E5DF", mx: "4px" }} />

              {/* Machine dropdown trigger */}
              <DropdownTrigger
                label={translate("serviceReports.filters.machine")}
                isActive={selectedMachineIds.size > 0}
                onClick={(e) => setMachineMenuAnchor(e.currentTarget)}
              />

              {/* Location dropdown trigger */}
              <DropdownTrigger
                label={translate("serviceReports.filters.location")}
                isActive={selectedLocations.size > 0}
                onClick={(e) => setLocationMenuAnchor(e.currentTarget)}
              />

              {/* Reported by dropdown trigger */}
              <DropdownTrigger
                label={translate("serviceReports.filters.reportedBy")}
                isActive={selectedReporters.size > 0}
                onClick={(e) => setReporterMenuAnchor(e.currentTarget)}
              />

              {/* Clear filters */}
              {hasActiveFilters && (
                <Box
                  onClick={clearAllFilters}
                  sx={{
                    marginLeft: "auto",
                    fontFamily: dt.font,
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "#9CA3AF",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    "&:hover": { color: "#D4572B" },
                  }}
                >
                  {translate("serviceReports.filters.clearFilters")}
                </Box>
              )}
            </Box>
          )}

          {/* ─── Row 3: Active Filter Chips ─── */}
          {!isPhone && activeChips.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                pb: "10px",
                flexWrap: "wrap",
                animation: "fadeSlideUp 0.25s ease both",
              }}
            >
              {activeChips.map((chip) => (
                <Box
                  key={`${chip.type}-${chip.value}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                    fontFamily: dt.font,
                    fontSize: "11px",
                    fontWeight: 500,
                    color: "#4A4F57",
                    backgroundColor: "#F2F0EB",
                    borderRadius: "14px",
                    padding: "4px 8px 4px 12px",
                    userSelect: "none",
                  }}
                >
                  {chip.label}
                  <Box
                    onClick={() => removeFilterChip(chip.type, chip.value)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "16px",
                      height: "16px",
                      borderRadius: "50%",
                      cursor: "pointer",
                      color: "#8C9099",
                      transition: "all 0.15s ease",
                      "&:hover": { backgroundColor: "rgba(0,0,0,0.06)", color: "#D4572B" },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: "12px" }} />
                  </Box>
                </Box>
              ))}
              <Box
                onClick={clearAllFilters}
                sx={{
                  fontFamily: dt.font,
                  fontSize: "11px",
                  fontWeight: 500,
                  color: "#D4572B",
                  cursor: "pointer",
                  userSelect: "none",
                  transition: "color 0.15s ease",
                  "&:hover": { color: "#B84A24" },
                }}
              >
                {translate("serviceReports.filters.clearAll")}
              </Box>
            </Box>
          )}

          {/* Phone: active filter chips (horizontal scroll) */}
          {isPhone && activeChips.length > 0 && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                pb: "8px",
                overflow: "auto",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {activeChips.map((chip) => (
                <Box
                  key={`${chip.type}-${chip.value}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "4px",
                    fontFamily: dt.font,
                    fontSize: "11px",
                    fontWeight: 500,
                    color: "#4A4F57",
                    backgroundColor: "#F2F0EB",
                    borderRadius: "14px",
                    padding: "3px 6px 3px 10px",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  {chip.label}
                  <Box
                    onClick={() => removeFilterChip(chip.type, chip.value)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "16px",
                      height: "16px",
                      borderRadius: "50%",
                      cursor: "pointer",
                      color: "#8C9099",
                      "&:hover": { color: "#D4572B" },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: "11px" }} />
                  </Box>
                </Box>
              ))}
              <Box
                onClick={clearAllFilters}
                sx={{
                  fontFamily: dt.font,
                  fontSize: "11px",
                  fontWeight: 500,
                  color: "#D4572B",
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                {translate("serviceReports.filters.clearAll")}
              </Box>
            </Box>
          )}
        </Box>{/* end White Bar */}

        {/* ─── Error Banner ─── */}
        {error && !loading && (
          <Alert
            severity="error"
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              borderRadius: "8px",
              mb: "12px",
              animation: "fadeSlideUp 0.35s ease both",
            }}
          >
            {error}
          </Alert>
        )}

        {/* ─── Card List ─── */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: isPhone ? "8px" : "10px",
            width: "100%",
            pb: "24px",
          }}
        >
          {showInitialLoading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <Skeleton
                key={i}
                variant="rounded"
                height={120}
                sx={{
                  borderRadius: "10px",
                  animation: `fadeSlideUp 0.35s ease ${i * 0.05}s both`,
                }}
              />
            ))
          ) : filteredItems.length === 0 ? (
            <EmptyState
              tab={activeTab}
              searchTerm={searchTerm}
              hasActiveFilters={hasActiveFilters}
              translate={translate}
              onClearFilters={clearAllFilters}
            />
          ) : (
            <>
              {/* "Collapse All" button — visible when 2+ cards expanded */}
              {!selectMode && expandedSrIds.size >= 2 && (
                <Box
                  onClick={() => setExpandedSrIds(new Set())}
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mb: "-4px",
                    cursor: "pointer",
                  }}
                >
                  <Box
                    sx={{
                      fontFamily: dt.font,
                      fontSize: "12px",
                      fontWeight: 500,
                      color: "#D4572B",
                      lineHeight: "16px",
                      "&:hover": { textDecoration: "underline", color: "#B84A24" },
                    }}
                  >
                    {translate("serviceReports.collapseAll")}
                  </Box>
                </Box>
              )}
              {filteredItems.map((item, index) => (
                <ServiceReportCard
                  key={item.sr.id}
                  item={item}
                  index={index}
                  isPhone={isPhone}
                  isTablet={isTablet}
                  hasServicingPermission={hasServicingPermission}
                  onCreateWorkOrder={handleCreateWorkOrder}
                  onCreateServiceRequest={handleCreateServiceRequest}
                  onViewOnBoard={handleViewOnBoard}
                  onViewInServiceHub={handleViewInServiceHub}
                  onOverflowAction={handleOverflowAction}
                  onUrgencyChange={handleUrgencyChange}
                  isExpanded={!selectMode && expandedSrIds.has(item.sr.id)}
                  onToggle={() => toggleExpand(item.sr.id)}
                  selectMode={selectMode}
                  isSelected={selectedSrIds.has(item.sr.id)}
                  onToggleSelect={() => toggleSelectSr(item.sr.id)}
                />
              ))}
            </>
          )}
        </Box>
      </Box>

      {/* ─── Backdrop overlay for WO panel ─── */}
      {woPanelOpen && (
        <Box
          onClick={handleWoPanelClose}
          sx={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(12,17,23,0.18)",
            zIndex: 1299,
            transition: "opacity 0.3s ease",
          }}
        />
      )}

      {/* ─── Create Work Order Side Panel ─── */}
      <CreateWorkOrderPanel
        open={woPanelOpen}
        onClose={handleWoPanelClose}
        onCreated={handleWoCreated}
        prefill={woPrefill}
      />

      {/* ─── Date Range Dropdown Menu ─── */}
      <Menu
        anchorEl={dateMenuAnchor}
        open={Boolean(dateMenuAnchor)}
        onClose={() => setDateMenuAnchor(null)}
        PaperProps={{
          sx: {
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            minWidth: "160px",
            mt: "4px",
          },
        }}
      >
        {DATE_RANGES.map((opt) => (
          <MenuItem
            key={opt.key}
            selected={dateRange === opt.key}
            onClick={() => { setDateRange(opt.key); setDateMenuAnchor(null); }}
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              fontWeight: dateRange === opt.key ? 700 : 500,
              color: dateRange === opt.key ? "#D4572B" : "#4A4F57",
              py: "8px",
            }}
          >
            {translate(opt.labelKey)}
          </MenuItem>
        ))}
      </Menu>

      {/* ─── Sort Dropdown Menu ─── */}
      <Menu
        anchorEl={sortMenuAnchor}
        open={Boolean(sortMenuAnchor)}
        onClose={() => setSortMenuAnchor(null)}
        PaperProps={{
          sx: {
            borderRadius: "8px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            minWidth: "160px",
            mt: "4px",
          },
        }}
      >
        {SORT_OPTIONS.map((opt) => (
          <MenuItem
            key={opt.key}
            selected={sortKey === opt.key}
            onClick={() => { setSortKey(opt.key); setSortMenuAnchor(null); }}
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              fontWeight: sortKey === opt.key ? 700 : 500,
              color: sortKey === opt.key ? "#D4572B" : "#4A4F57",
              py: "8px",
              display: "flex",
              justifyContent: "space-between",
              gap: "16px",
            }}
          >
            {translate(opt.labelKey)}
            {sortKey === opt.key && <CheckIcon sx={{ fontSize: "16px", color: "#D4572B" }} />}
          </MenuItem>
        ))}
      </Menu>

      {/* ─── Machine Dropdown ─── */}
      <Menu
        anchorEl={machineMenuAnchor}
        open={Boolean(machineMenuAnchor)}
        onClose={() => { setMachineMenuAnchor(null); setMachineSearch(""); }}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
            minWidth: "280px",
            maxHeight: "360px",
            mt: "4px",
            p: "8px 0",
          },
        }}
      >
        {/* Search field */}
        <Box sx={{ px: "12px", pb: "8px" }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder={translate("serviceReports.filters.searchMachines")}
            value={machineSearch}
            onChange={(e) => setMachineSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: "15px", color: "#B8B5AF" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontFamily: dt.font,
                fontSize: "12px",
                backgroundColor: "#F7F5F0",
                borderRadius: "8px",
                height: "34px",
                "& fieldset": { borderColor: "transparent" },
                "&:hover fieldset": { borderColor: "#E8E5DF" },
                "&.Mui-focused fieldset": { borderColor: "#D4572B", borderWidth: "1.5px" },
              },
            }}
          />
        </Box>
        {machines
          .filter((m) =>
            !machineSearch.trim() ||
            m.model.toLowerCase().includes(machineSearch.toLowerCase()) ||
            m.sn.toLowerCase().includes(machineSearch.toLowerCase())
          )
          .map((m) => {
            const isChecked = selectedMachineIds.has(m.id);
            return (
              <MenuItem
                key={m.id}
                onClick={() => toggleMachine(m.id)}
                sx={{
                  fontFamily: dt.font,
                  fontSize: "13px",
                  py: "8px",
                  px: "12px",
                  backgroundColor: isChecked ? "rgba(212,87,43,0.05)" : "transparent",
                  "&:hover": { backgroundColor: isChecked ? "rgba(212,87,43,0.08)" : "#F7F5F0" },
                }}
              >
                <Checkbox
                  size="small"
                  checked={isChecked}
                  sx={{
                    p: "2px",
                    mr: "8px",
                    color: "#D1CCC4",
                    "&.Mui-checked": { color: "#D4572B" },
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ fontWeight: isChecked ? 600 : 500, color: "#0C1117" }}>
                    {m.model}
                  </Box>
                  {m.sn && (
                    <Box sx={{ fontFamily: dt.fontMono, fontSize: "11px", color: "#8C9099", mt: "1px" }}>
                      SN:{m.sn}
                    </Box>
                  )}
                </Box>
                <Box sx={{ fontFamily: dt.fontMono, fontSize: "11px", color: "#8C9099", ml: "12px", whiteSpace: "nowrap" }}>
                  {translate("serviceReports.filters.reportCount", { count: m.count })}
                </Box>
              </MenuItem>
            );
          })}
        {machines.length === 0 && (
          <Box sx={{ px: "16px", py: "12px", fontFamily: dt.font, fontSize: "12px", color: "#8C9099", textAlign: "center" }}>
            {translate("machineSidebar.noMachines")}
          </Box>
        )}
      </Menu>

      {/* ─── Location Dropdown ─── */}
      <Menu
        anchorEl={locationMenuAnchor}
        open={Boolean(locationMenuAnchor)}
        onClose={() => setLocationMenuAnchor(null)}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
            minWidth: "260px",
            maxHeight: "360px",
            mt: "4px",
            p: "8px 0",
          },
        }}
      >
        {Array.from(locations.entries()).map(([parentName, parent]) => (
          <Box key={parentName}>
            {/* Parent header */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                px: "12px",
                py: "8px",
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 600,
                color: "#0C1117",
                cursor: "pointer",
                "&:hover": { backgroundColor: "#F7F5F0" },
              }}
            >
              <Checkbox
                size="small"
                checked={Array.from(parent.children.values()).every((c) => selectedLocations.has(c.name))}
                indeterminate={
                  Array.from(parent.children.values()).some((c) => selectedLocations.has(c.name)) &&
                  !Array.from(parent.children.values()).every((c) => selectedLocations.has(c.name))
                }
                onChange={() => {
                  const childNames = Array.from(parent.children.values()).map((c) => c.name);
                  const allSelected = childNames.every((n) => selectedLocations.has(n));
                  setSelectedLocations((prev) => {
                    const next = new Set(prev);
                    if (allSelected) {
                      childNames.forEach((n) => next.delete(n));
                    } else {
                      childNames.forEach((n) => next.add(n));
                    }
                    return next;
                  });
                }}
                sx={{
                  p: "2px",
                  mr: "8px",
                  color: "#D1CCC4",
                  "&.Mui-checked": { color: "#D4572B" },
                  "&.MuiCheckbox-indeterminate": { color: "#D4572B" },
                }}
              />
              {parentName}
              <ArrowDownIcon sx={{ fontSize: "16px", ml: "auto", color: "#8C9099" }} />
            </Box>
            {/* Children */}
            {Array.from(parent.children.entries()).map(([childName, child]) => {
              if (childName === parentName) return null; // Skip if child = parent (top-level location)
              const isChecked = selectedLocations.has(childName);
              return (
                <MenuItem
                  key={childName}
                  onClick={() => toggleLocation(childName)}
                  sx={{
                    fontFamily: dt.font,
                    fontSize: "12px",
                    py: "6px",
                    pl: "44px",
                    pr: "12px",
                    color: "#4A4F57",
                    backgroundColor: isChecked ? "rgba(212,87,43,0.05)" : "transparent",
                    "&:hover": { backgroundColor: isChecked ? "rgba(212,87,43,0.08)" : "#F7F5F0" },
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={isChecked}
                    sx={{
                      p: "2px",
                      mr: "8px",
                      color: "#D1CCC4",
                      "&.Mui-checked": { color: "#D4572B" },
                    }}
                  />
                  {childName}
                  <Box sx={{ fontFamily: dt.fontMono, fontSize: "11px", color: "#8C9099", ml: "auto", pl: "12px" }}>
                    ({child.count})
                  </Box>
                </MenuItem>
              );
            })}
            {/* If the only child IS the parent (top-level location), show it directly */}
            {parent.children.size === 1 && parent.children.has(parentName) && (
              (() => {
                const child = parent.children.get(parentName)!;
                const isChecked = selectedLocations.has(parentName);
                return (
                  <MenuItem
                    key={`${parentName}-self`}
                    onClick={() => toggleLocation(parentName)}
                    sx={{
                      fontFamily: dt.font,
                      fontSize: "12px",
                      py: "6px",
                      pl: "44px",
                      pr: "12px",
                      color: "#4A4F57",
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={isChecked}
                      sx={{ p: "2px", mr: "8px", color: "#D1CCC4", "&.Mui-checked": { color: "#D4572B" } }}
                    />
                    {translate("serviceReports.filters.selectLocation")}
                    <Box sx={{ fontFamily: dt.fontMono, fontSize: "11px", color: "#8C9099", ml: "auto", pl: "12px" }}>
                      ({child.count})
                    </Box>
                  </MenuItem>
                );
              })()
            )}
          </Box>
        ))}
        {locations.size === 0 && (
          <Box sx={{ px: "16px", py: "12px", fontFamily: dt.font, fontSize: "12px", color: "#8C9099", textAlign: "center" }}>
            {translate("locationSidebar.noLocations")}
          </Box>
        )}
      </Menu>

      {/* ─── Reported By Dropdown ─── */}
      <Menu
        anchorEl={reporterMenuAnchor}
        open={Boolean(reporterMenuAnchor)}
        onClose={() => { setReporterMenuAnchor(null); setReporterSearch(""); }}
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
            minWidth: "260px",
            maxHeight: "360px",
            mt: "4px",
            p: "8px 0",
          },
        }}
      >
        {/* Search field */}
        <Box sx={{ px: "12px", pb: "8px" }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder={translate("serviceReports.filters.searchPeople")}
            value={reporterSearch}
            onChange={(e) => setReporterSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: "15px", color: "#B8B5AF" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontFamily: dt.font,
                fontSize: "12px",
                backgroundColor: "#F7F5F0",
                borderRadius: "8px",
                height: "34px",
                "& fieldset": { borderColor: "transparent" },
                "&:hover fieldset": { borderColor: "#E8E5DF" },
                "&.Mui-focused fieldset": { borderColor: "#D4572B", borderWidth: "1.5px" },
              },
            }}
          />
        </Box>
        {reporters
          .filter((r) =>
            !reporterSearch.trim() ||
            r.name.toLowerCase().includes(reporterSearch.toLowerCase())
          )
          .map((r) => {
            const isChecked = selectedReporters.has(r.name);
            return (
              <MenuItem
                key={r.name}
                onClick={() => toggleReporter(r.name)}
                sx={{
                  fontFamily: dt.font,
                  fontSize: "13px",
                  py: "8px",
                  px: "12px",
                  backgroundColor: isChecked ? "rgba(212,87,43,0.05)" : "transparent",
                  "&:hover": { backgroundColor: isChecked ? "rgba(212,87,43,0.08)" : "#F7F5F0" },
                }}
              >
                <Checkbox
                  size="small"
                  checked={isChecked}
                  sx={{
                    p: "2px",
                    mr: "8px",
                    color: "#D1CCC4",
                    "&.Mui-checked": { color: "#D4572B" },
                  }}
                />
                {/* Avatar */}
                <Box
                  sx={{
                    width: "24px",
                    height: "24px",
                    borderRadius: "50%",
                    backgroundColor: getAvatarColor(r.name),
                    color: "#FFFFFF",
                    fontSize: "10px",
                    fontWeight: 700,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mr: "8px",
                    flexShrink: 0,
                    fontFamily: dt.font,
                  }}
                >
                  {getInitials(r.name)}
                </Box>
                <Box sx={{ flex: 1, fontWeight: isChecked ? 600 : 500, color: "#0C1117" }}>
                  {r.name}
                </Box>
                <Box sx={{ fontFamily: dt.fontMono, fontSize: "11px", color: "#8C9099", ml: "12px", whiteSpace: "nowrap" }}>
                  {translate("serviceReports.filters.reportCount", { count: r.count })}
                </Box>
              </MenuItem>
            );
          })}
        {reporters.length === 0 && (
          <Box sx={{ px: "16px", py: "12px", fontFamily: dt.font, fontSize: "12px", color: "#8C9099", textAlign: "center" }}>
            {translate("serviceReports.filters.noReporters")}
          </Box>
        )}
      </Menu>

      {/* ─── Phone: Filter Bottom Sheet ─── */}
      <Drawer
        anchor="bottom"
        open={phoneFiltersOpen}
        onClose={() => setPhoneFiltersOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: "16px 16px 0 0",
            maxHeight: "85vh",
            background: "#FFFFFF !important",
            backgroundColor: "#FFFFFF !important",
            color: "#0C1117 !important",
          },
        }}
      >
        <Box sx={{ px: "20px", pt: "12px", pb: "24px" }}>
          {/* Drag handle */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: "16px" }}>
            <Box sx={{ width: "36px", height: "4px", borderRadius: "2px", backgroundColor: "#E8E5DF" }} />
          </Box>

          {/* Header */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "20px" }}>
            <Box sx={{ fontFamily: dt.font, fontSize: "18px", fontWeight: 700, color: "#0C1117" }}>
              {translate("serviceReports.filters.filtersTitle")}
            </Box>
            <Box
              onClick={resetPhoneFilters}
              sx={{
                fontFamily: dt.font,
                fontSize: "13px",
                fontWeight: 500,
                color: "#8C9099",
                cursor: "pointer",
                "&:hover": { color: "#D4572B" },
              }}
            >
              {translate("serviceReports.filters.clearAll")}
            </Box>
          </Box>

          {/* Urgency section */}
          <Box sx={{ mb: "20px" }}>
            <Box sx={{ fontFamily: dt.font, fontSize: "11px", fontWeight: 700, color: "#8C9099", textTransform: "uppercase", letterSpacing: "1.2px", mb: "10px" }}>
              {translate("serviceReports.urgency.critical").replace("CRITICAL", "URGENCY")}
            </Box>
            <Box sx={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
              {URGENCY_CHIPS.map((chip) => {
                const isSelected = tempUrgencies.has(chip.value);
                return (
                  <Box
                    key={chip.value}
                    onClick={() => toggleTempUrgency(chip.value)}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "6px 16px",
                      borderRadius: "20px",
                      border: isSelected ? "1.5px solid #0C1117" : "1.5px solid #E8E5DF",
                      backgroundColor: isSelected ? "#0C1117" : "#FFFFFF",
                      color: isSelected ? "#FFFFFF" : "#444F57",
                      fontFamily: dt.font,
                      fontSize: "13px",
                      fontWeight: 500,
                      cursor: "pointer",
                      userSelect: "none",
                      height: "32px",
                      boxSizing: "border-box",
                    }}
                  >
                    <Box sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      backgroundColor: isSelected ? "#FFFFFF" : chip.color,
                      flexShrink: 0,
                      border: isSelected ? "none" : `1.5px solid ${chip.color}`,
                      boxSizing: "border-box",
                    }} />
                    {translate(chip.labelKey)}
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Machine section */}
          <Box sx={{ mb: "20px" }}>
            <Box sx={{ fontFamily: dt.font, fontSize: "11px", fontWeight: 700, color: "#8C9099", textTransform: "uppercase", letterSpacing: "1.2px", mb: "10px" }}>
              {translate("serviceReports.filters.machine")}
            </Box>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "14px",
                color: tempMachineIds.size > 0 ? "#0C1117" : "#8C9099",
                backgroundColor: "#F7F5F0",
                borderRadius: "10px",
                padding: "12px 14px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
              onClick={() => {
                // For phone, cycle through machines
                // Simple: just show first machine or placeholder
              }}
            >
              {tempMachineIds.size > 0
                ? machines.filter((m) => tempMachineIds.has(m.id)).map((m) => m.model).join(", ")
                : translate("serviceReports.filters.selectMachine")}
              <ArrowDownIcon sx={{ fontSize: "18px", color: "#8C9099" }} />
            </Box>
            {/* Simple inline machine list for phone */}
            {machines.length > 0 && (
              <Box sx={{ mt: "8px", maxHeight: "120px", overflow: "auto" }}>
                {machines.map((m) => {
                  const isChecked = tempMachineIds.has(m.id);
                  return (
                    <Box
                      key={m.id}
                      onClick={() => {
                        setTempMachineIds((prev) => {
                          const next = new Set(prev);
                          if (next.has(m.id)) next.delete(m.id);
                          else next.add(m.id);
                          return next;
                        });
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        py: "6px",
                        px: "4px",
                        cursor: "pointer",
                        borderRadius: "6px",
                        "&:hover": { backgroundColor: "#F7F5F0" },
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={isChecked}
                        sx={{ p: "2px", color: "#D1CCC4", "&.Mui-checked": { color: "#D4572B" } }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ fontFamily: dt.font, fontSize: "13px", fontWeight: isChecked ? 600 : 500, color: "#0C1117" }}>{m.model}</Box>
                        {m.sn && <Box sx={{ fontFamily: dt.fontMono, fontSize: "10px", color: "#8C9099" }}>SN:{m.sn}</Box>}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>

          {/* Location section */}
          <Box sx={{ mb: "20px" }}>
            <Box sx={{ fontFamily: dt.font, fontSize: "11px", fontWeight: 700, color: "#8C9099", textTransform: "uppercase", letterSpacing: "1.2px", mb: "10px" }}>
              {translate("serviceReports.filters.location")}
            </Box>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "14px",
                color: tempLocations.size > 0 ? "#0C1117" : "#8C9099",
                backgroundColor: "#F7F5F0",
                borderRadius: "10px",
                padding: "12px 14px",
              }}
            >
              {tempLocations.size > 0
                ? Array.from(tempLocations).join(", ")
                : translate("serviceReports.filters.selectLocation")}
            </Box>
            {flatLocationNames.length > 0 && (
              <Box sx={{ mt: "8px", maxHeight: "120px", overflow: "auto" }}>
                {flatLocationNames.map((name) => {
                  const isChecked = tempLocations.has(name);
                  return (
                    <Box
                      key={name}
                      onClick={() => {
                        setTempLocations((prev) => {
                          const next = new Set(prev);
                          if (next.has(name)) next.delete(name);
                          else next.add(name);
                          return next;
                        });
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        py: "6px",
                        px: "4px",
                        cursor: "pointer",
                        borderRadius: "6px",
                        "&:hover": { backgroundColor: "#F7F5F0" },
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={isChecked}
                        sx={{ p: "2px", color: "#D1CCC4", "&.Mui-checked": { color: "#D4572B" } }}
                      />
                      <Box sx={{ fontFamily: dt.font, fontSize: "13px", fontWeight: isChecked ? 600 : 500, color: "#0C1117" }}>{name}</Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>

          {/* Reported by section */}
          <Box sx={{ mb: "20px" }}>
            <Box sx={{ fontFamily: dt.font, fontSize: "11px", fontWeight: 700, color: "#8C9099", textTransform: "uppercase", letterSpacing: "1.2px", mb: "10px" }}>
              {translate("serviceReports.filters.reportedBy")}
            </Box>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "14px",
                color: tempReporters.size > 0 ? "#0C1117" : "#8C9099",
                backgroundColor: "#F7F5F0",
                borderRadius: "10px",
                padding: "12px 14px",
              }}
            >
              {tempReporters.size > 0
                ? Array.from(tempReporters).join(", ")
                : translate("serviceReports.filters.selectPerson")}
            </Box>
            {reporters.length > 0 && (
              <Box sx={{ mt: "8px", maxHeight: "120px", overflow: "auto" }}>
                {reporters.map((r) => {
                  const isChecked = tempReporters.has(r.name);
                  return (
                    <Box
                      key={r.name}
                      onClick={() => {
                        setTempReporters((prev) => {
                          const next = new Set(prev);
                          if (next.has(r.name)) next.delete(r.name);
                          else next.add(r.name);
                          return next;
                        });
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        py: "6px",
                        px: "4px",
                        cursor: "pointer",
                        borderRadius: "6px",
                        "&:hover": { backgroundColor: "#F7F5F0" },
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={isChecked}
                        sx={{ p: "2px", color: "#D1CCC4", "&.Mui-checked": { color: "#D4572B" } }}
                      />
                      <Box
                        sx={{
                          width: "22px",
                          height: "22px",
                          borderRadius: "50%",
                          backgroundColor: getAvatarColor(r.name),
                          color: "#FFFFFF",
                          fontSize: "9px",
                          fontWeight: 700,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          fontFamily: dt.font,
                        }}
                      >
                        {getInitials(r.name)}
                      </Box>
                      <Box sx={{ fontFamily: dt.font, fontSize: "13px", fontWeight: isChecked ? 600 : 500, color: "#0C1117" }}>{r.name}</Box>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>

          {/* Sort section */}
          <Box sx={{ mb: "24px" }}>
            <Box sx={{ fontFamily: dt.font, fontSize: "11px", fontWeight: 700, color: "#8C9099", textTransform: "uppercase", letterSpacing: "1.2px", mb: "10px" }}>
              {translate("serviceReports.sort.label").replace(":", "")}
            </Box>
            <Box
              sx={{
                fontFamily: dt.font,
                fontSize: "14px",
                fontWeight: 600,
                color: "#0C1117",
                backgroundColor: "#F7F5F0",
                borderRadius: "10px",
                padding: "12px 14px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              {translate(SORT_OPTIONS.find((s) => s.key === tempSortKey)?.labelKey || "")}
              <ArrowDownIcon sx={{ fontSize: "18px", color: "#8C9099" }} />
            </Box>
            <Box sx={{ mt: "8px" }}>
              {SORT_OPTIONS.map((opt) => {
                const isActive = tempSortKey === opt.key;
                return (
                  <Box
                    key={opt.key}
                    onClick={() => setTempSortKey(opt.key)}
                    sx={{
                      fontFamily: dt.font,
                      fontSize: "13px",
                      fontWeight: isActive ? 600 : 500,
                      color: isActive ? "#D4572B" : "#4A4F57",
                      py: "8px",
                      px: "4px",
                      cursor: "pointer",
                      borderRadius: "6px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      "&:hover": { backgroundColor: "#F7F5F0" },
                    }}
                  >
                    {translate(opt.labelKey)}
                    {isActive && <CheckIcon sx={{ fontSize: "16px", color: "#D4572B" }} />}
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Apply Filters button */}
          <Button
            fullWidth
            onClick={applyPhoneFilters}
            sx={{
              fontFamily: dt.font,
              fontSize: "15px",
              fontWeight: 700,
              textTransform: "none",
              color: "#FFFFFF",
              backgroundColor: "#D4572B",
              borderRadius: "24px",
              height: "48px",
              mb: "10px",
              "&:hover": { backgroundColor: "#B84A24" },
            }}
          >
            {translate("serviceReports.filters.applyFilters")}
          </Button>

          {/* Reset link */}
          <Box
            onClick={resetPhoneFilters}
            sx={{
              fontFamily: dt.font,
              fontSize: "13px",
              fontWeight: 500,
              color: "#8C9099",
              textAlign: "center",
              cursor: "pointer",
              "&:hover": { color: "#D4572B" },
            }}
          >
            {translate("serviceReports.filters.reset")}
          </Box>
        </Box>
      </Drawer>

      {/* ─── SR Action Dialogs ──────────────────────────────── */}

      {/* Resolve Dialog */}
      {actionTargetItem && (
        <ResolveSrDialog
          open={resolveDialogOpen}
          item={actionTargetItem}
          onClose={() => { setResolveDialogOpen(false); setActionTargetItem(null); }}
          onResolved={handleResolved}
          translate={translate}
        />
      )}

      {/* Delete Dialog */}
      {actionTargetItem && (
        <DeleteSrDialog
          open={deleteDialogOpen}
          item={actionTargetItem}
          onClose={() => { setDeleteDialogOpen(false); setActionTargetItem(null); }}
          onDeleted={handleDeleted}
          translate={translate}
        />
      )}

      {/* Edit Side Panel */}
      {actionTargetItem && (
        <EditSrPanel
          open={editPanelOpen}
          item={actionTargetItem}
          onClose={() => { setEditPanelOpen(false); setActionTargetItem(null); }}
          onUpdated={handleUpdated}
          translate={translate}
        />
      )}

      {/* Link to WO Side Panel */}
      {actionTargetItem && (
        <LinkToWoPanel
          open={linkToWoPanelOpen}
          item={actionTargetItem}
          onClose={() => { setLinkToWoPanelOpen(false); setActionTargetItem(null); }}
          onLinked={handleLinked}
          translate={translate}
        />
      )}

      {/* ─── Bulk Select Floating Bar ─────────────────────────── */}
      {selectMode && (
        <BulkSelectFloatingBar
          selectedCount={selectedSrIds.size}
          isPhone={isPhone}
          isTablet={isTablet}
          activeTab={activeTab}
          onAction={handleBulkAction}
          onClear={clearSelection}
          translate={translate}
        />
      )}

      {/* ─── Bulk Resolve Dialog ─────────────────────────────── */}
      <BulkResolveDialog
        open={bulkResolveOpen}
        items={selectedItems}
        onClose={() => setBulkResolveOpen(false)}
        onResolved={handleBulkResolved}
        translate={translate}
      />

      {/* ─── Bulk Delete Dialog ──────────────────────────────── */}
      <BulkDeleteDialog
        open={bulkDeleteOpen}
        count={selectedSrIds.size}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={handleBulkDeleteConfirm}
        translate={translate}
      />

      {/* ─── Bulk Reopen Dialog ─────────────────────────────── */}
      <BulkReopenDialog
        open={bulkReopenOpen}
        items={selectedItems}
        onClose={() => setBulkReopenOpen(false)}
        onReopened={handleBulkReopened}
        translate={translate}
      />

      {/* ─── Bulk Unlink Dialog ─────────────────────────────── */}
      <BulkUnlinkDialog
        open={bulkUnlinkOpen}
        items={selectedItems}
        onClose={() => setBulkUnlinkOpen(false)}
        onUnlinked={handleBulkUnlinked}
        translate={translate}
      />

      {/* ─── Bulk Create WO Panel ────────────────────────────── */}
      {bulkWoPanelOpen && (
        <Box
          onClick={handleBulkWoPanelClose}
          sx={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(12,17,23,0.18)",
            zIndex: 1299,
            transition: "opacity 0.3s ease",
          }}
        />
      )}
      <CreateWorkOrderPanel
        open={bulkWoPanelOpen}
        onClose={handleBulkWoPanelClose}
        onCreated={handleBulkWoCreated}
        prefill={bulkWoPrefill}
      />

      {/* SR Action Toast Stack */}
      <ToastStack toasts={toasts} onDismiss={dismissToast} />
    </Box>
  );
};

// ─── Sub-components ──────────────────────────────────────────

/** Filter dropdown trigger button (desktop) */
interface DropdownTriggerProps {
  label: string;
  isActive: boolean;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
}

const DropdownTrigger: React.FC<DropdownTriggerProps> = ({ label, isActive, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
      padding: "6px 14px",
      borderRadius: "8px",
      border: "none",
      backgroundColor: isActive ? "#0C1117" : "#F7F5F0",
      color: isActive ? "#FFFFFF" : "#8C9099",
      fontFamily: dt.font,
      fontSize: "13px",
      fontWeight: 500,
      cursor: "pointer",
      transition: "all 0.15s ease",
      whiteSpace: "nowrap",
      userSelect: "none",
      height: "32px",
      boxSizing: "border-box",
      "&:hover": {
        backgroundColor: isActive ? "#1a1a2e" : "#EDEAE5",
        color: isActive ? "#FFFFFF" : "#444F57",
      },
    }}
  >
    {label}
    <ArrowDownIcon sx={{ fontSize: 14, color: isActive ? "rgba(255,255,255,0.8)" : "#8C9099" }} />
  </Box>
);

interface EmptyStateProps {
  tab: SrTab;
  searchTerm: string;
  hasActiveFilters: boolean;
  translate: (key: string) => string;
  onClearFilters: () => void;
}

const EmptyState: React.FC<EmptyStateProps> = ({ tab, searchTerm, hasActiveFilters, translate, onClearFilters }) => {
  if (searchTerm.trim()) {
    return (
      <Box sx={{ textAlign: "center", py: "60px", animation: "fadeSlideUp 0.35s ease both" }}>
        <SearchIcon sx={{ fontSize: "40px", color: "#D1CCC4", mb: "12px" }} />
        <Box sx={{ fontFamily: dt.font, fontSize: "15px", fontWeight: 600, color: "#4A4F57" }}>
          {translate("serviceReports.empty.noSearchResults")}
        </Box>
        <Box sx={{ fontFamily: dt.font, fontSize: "13px", color: "#8C9099", mt: "4px" }}>
          {translate("serviceReports.empty.tryDifferentSearch")}
        </Box>
      </Box>
    );
  }

  if (hasActiveFilters) {
    return (
      <Box sx={{ textAlign: "center", py: "60px", animation: "fadeSlideUp 0.35s ease both" }}>
        <FilterListIcon sx={{ fontSize: "40px", color: "#D1CCC4", mb: "12px" }} />
        <Box sx={{ fontFamily: dt.font, fontSize: "15px", fontWeight: 600, color: "#4A4F57" }}>
          {translate("serviceReports.empty.noFilterResults")}
        </Box>
        <Box sx={{ fontFamily: dt.font, fontSize: "13px", color: "#8C9099", mt: "4px" }}>
          {translate("serviceReports.empty.tryDifferentFilters")}
        </Box>
        <Box
          onClick={onClearFilters}
          sx={{
            fontFamily: dt.font,
            fontSize: "13px",
            fontWeight: 600,
            color: "#D4572B",
            cursor: "pointer",
            mt: "12px",
            "&:hover": { color: "#B84A24" },
          }}
        >
          {translate("serviceReports.filters.clearAll")}
        </Box>
      </Box>
    );
  }

  if (tab === "untriaged") {
    return (
      <Box sx={{ textAlign: "center", py: "60px", animation: "fadeSlideUp 0.35s ease both" }}>
        <Box
          sx={{
            width: "56px",
            height: "56px",
            borderRadius: "50%",
            backgroundColor: "rgba(212,87,43,0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: "16px",
          }}
        >
          <AllClearIcon sx={{ fontSize: "28px", color: "#D4572B" }} />
        </Box>
        <Box sx={{ fontFamily: dt.font, fontSize: "16px", fontWeight: 700, color: "#0C1117" }}>
          {translate("serviceReports.empty.allClear")}
        </Box>
        <Box sx={{ fontFamily: dt.font, fontSize: "13px", color: "#8C9099", mt: "6px" }}>
          {translate("serviceReports.empty.noPendingReports")}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ textAlign: "center", py: "60px", animation: "fadeSlideUp 0.35s ease both" }}>
      <Box sx={{ fontFamily: dt.font, fontSize: "15px", fontWeight: 600, color: "#4A4F57" }}>
        {translate("serviceReports.empty.noItems")}
      </Box>
      <Box sx={{ fontFamily: dt.font, fontSize: "13px", color: "#8C9099", mt: "4px" }}>
        {translate("serviceReports.empty.noItemsDescription")}
      </Box>
    </Box>
  );
};

export default ServiceReportsPage;
